(() => {
  'use strict';
  if (window.__csMaisonSignature) return;
  window.__csMaisonSignature = true;

  const skip = '.c-maison-signature,[data-cs-maison-wordmark],[data-cs-maison-skip],script,style,noscript,template,textarea,input,select,option,svg,math,pre,code,[contenteditable],.sr-only,.c-sr-only,.visually-hidden';
  const inlineTags = new Set(['SPAN', 'EM', 'STRONG', 'B', 'I']);
  const phrase = /Maison([\s\u00a0]+)Consiglieri/gi;
  const wordCharacter = /[\p{L}\p{N}_]/u;
  const pending = new Set();
  let scheduled = false;
  let observer;

  function isExcluded(element) {
    return !element || Boolean(element.closest(skip));
  }

  function inline(element, style) {
    return inlineTags.has(element.tagName) &&
      !element.matches('[role],[tabindex],a,button,[onclick]') &&
      (style.display === 'inline' || style.display === 'contents');
  }

  function commonAncestor(a, b) {
    let element = a.parentElement;
    while (element && !element.contains(b)) element = element.parentElement;
    return element;
  }

  function endpoint(node, offset, ancestor, start) {
    let current = node;
    while (current.parentNode !== ancestor) {
      if (start ? offset !== 0 || current.previousSibling : offset !== current.textContent.length || current.nextSibling) return null;
      current = current.parentNode;
      offset = start ? 0 : current.textContent.length;
    }
    return current;
  }

  function findPosition(nodes, offset, end) {
    let count = 0;
    for (const node of nodes) {
      const next = count + node.data.length;
      if (offset < next || (end && offset === next)) return { node, offset: offset - count };
      count = next;
    }
    return null;
  }

  function layoutFor(node) {
    const hint = node.parentElement.closest('[data-cs-maison-layout]');
    const requested = hint && hint.getAttribute('data-cs-maison-layout');
    if (requested === 'inline' || requested === 'signature') return requested;
    for (let element = node.parentElement; element && element !== document.body; element = element.parentElement) {
      if (/^(P|H[1-6]|FIGCAPTION|LI|SUMMARY)$/.test(element.tagName) || (element.tagName === 'DIV' && (element.children.length === 0 || (element.children.length === 1 && element.firstElementChild.hasAttribute('data-cs-maison-text'))))) {
        return /^[\p{P}\s]*Maison[\s\u00a0]+Consiglieri[\p{P}\s]*$/iu.test(element.textContent) ? 'signature' : 'inline';
      }
    }
    return 'inline';
  }

  function replaceMatch(nodes, start, match) {
    const first = findPosition(nodes, start, false);
    const last = findPosition(nodes, start + match[0].length, true);
    if (!first || !last || !first.node.isConnected || !last.node.isConnected) return;
    const ancestor = commonAncestor(first.node, last.node);
    if (!ancestor) return;
    const left = endpoint(first.node, first.offset, ancestor, true);
    const right = endpoint(last.node, last.offset, ancestor, false);
    if (!left || !right) return;
    const cuts = [6, 6 + match[1].length, match[0].length];
    let position = 0;
    for (let node = left; node; node = node.nextSibling) {
      let length = node.textContent.length;
      if (node === first.node) length -= first.offset;
      if (node === last.node) length -= last.node.data.length - last.offset;
      if (node.nodeType === Node.ELEMENT_NODE && cuts.some(cut => position < cut && position + length > cut)) return;
      position += length;
      if (node === right) break;
    }
    if (position !== match[0].length) return;
    const layout = layoutFor(first.node);

    let endNode = right;
    if (right === last.node && last.offset < last.node.data.length) last.node.splitText(last.offset);
    let startNode = left;
    if (left === first.node && first.offset > 0) {
      startNode = first.node.splitText(first.offset);
      if (left === right) endNode = startNode;
    }
    const selected = [];
    for (let node = startNode; node; node = node.nextSibling) {
      selected.push(node);
      if (node === endNode) break;
    }
    const wrapper = document.createElement('span');
    wrapper.className = 'cs-maison-wordmark';
    wrapper.setAttribute('data-cs-maison-wordmark', '');
    wrapper.setAttribute('data-cs-maison-layout', layout);
    const maison = document.createElement('span');
    maison.className = 'cs-maison-wordmark__maison';
    const name = document.createElement('span');
    name.className = 'cs-maison-wordmark__name';
    wrapper.append(maison, document.createTextNode(''), name);
    const gap = wrapper.childNodes[1];
    ancestor.insertBefore(wrapper, startNode);
    position = 0;
    for (const selectedNode of selected) {
      let node = selectedNode;
      while (node) {
        const part = position < cuts[0] ? 0 : position < cuts[1] ? 1 : 2;
        const available = cuts[part] - position;
        let remainder = null;
        if (node.nodeType === Node.TEXT_NODE && node.data.length > available) remainder = node.splitText(available);
        position += node.textContent.length;
        if (part === 0) maison.appendChild(node);
        else if (part === 2) name.appendChild(node);
        else if (node.nodeType === Node.TEXT_NODE) {
          gap.data += node.data;
          node.remove();
        } else wrapper.insertBefore(node, name);
        node = remainder;
      }
    }
  }

  function enhanceRun(nodes) {
    const content = nodes.map(node => node.data).join('');
    if (!/maison/i.test(content)) return;
    const matches = [];
    phrase.lastIndex = 0;
    let match;
    while ((match = phrase.exec(content))) {
      const before = Array.from(content.slice(0, match.index)).pop() || '';
      const after = Array.from(content.slice(match.index + match[0].length))[0] || '';
      if (!wordCharacter.test(before) && !wordCharacter.test(after)) matches.push(match);
    }
    for (let i = matches.length - 1; i >= 0; i--) replaceMatch(nodes, matches[i].index, matches[i]);
  }

  function scan(root) {
    if (!root.isConnected || isExcluded(root)) return;
    const styles = new WeakMap();
    const styleOf = element => {
      if (!styles.has(element)) styles.set(element, getComputedStyle(element));
      return styles.get(element);
    };
    for (let element = root; element; element = element.parentElement) {
      if (element.getAttribute('aria-hidden') === 'true') return;
    }
    function visit(container) {
      if (!/maison/i.test(container.textContent)) return;
      let run = [];
      const flush = () => { if (run.length) enhanceRun(run); run = []; };
      function descend(element) {
        for (const child of Array.from(element.childNodes)) {
          if (child.nodeType === Node.TEXT_NODE) {
            const display = styleOf(element).display;
            if ((display === 'flex' || display === 'inline-flex') && !element.hasAttribute('data-cs-maison-text') && /(^|[^\p{L}\p{N}_])Maison[\s\u00a0]+Consiglieri(?=$|[^\p{L}\p{N}_])/iu.test(child.data)) {
              flush();
              const label = document.createElement('span');
              label.className = 'cs-maison-text';
              label.setAttribute('data-cs-maison-text', '');
              element.insertBefore(label, child);
              label.appendChild(child);
              visit(label);
            } else run.push(child);
            continue;
          }
          if (child.nodeType !== Node.ELEMENT_NODE) { flush(); continue; }
          if (isExcluded(child)) { flush(); continue; }
          const style = styleOf(child);
          if (child.getAttribute('aria-hidden') === 'true') { flush(); continue; }
          if (inline(child, style)) descend(child);
          else { flush(); visit(child); }
        }
      }
      descend(container);
      flush();
    }
    visit(root);
  }

  function scope(node) {
    let element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    if (!element || isExcluded(element)) return null;
    while (element.parentElement && inline(element, getComputedStyle(element))) element = element.parentElement;
    return element;
  }

  function enqueue(node) {
    const element = scope(node);
    if (element && element.isConnected) pending.add(element);
  }

  function collect(records) {
    for (const record of records) {
      if (record.type === 'characterData') enqueue(record.target);
      else if (record.type === 'attributes') {
        if (record.target !== document.body && record.target !== document.documentElement) enqueue(record.target);
      } else for (const node of record.addedNodes) enqueue(node);
    }
  }

  const observe = () => observer.observe(document.body, {
    subtree: true, childList: true, characterData: true, attributes: true,
    attributeFilter: ['hidden', 'aria-hidden', 'class', 'style']
  });

  function flush() {
    scheduled = false;
    collect(observer.takeRecords());
    observer.disconnect();
    const roots = Array.from(pending).filter(root => root.isConnected);
    pending.clear();
    try {
      for (const root of roots) if (!roots.some(other => other !== root && other.contains(root))) scan(root);
    } finally { observe(); }
  }

  function start() {
    scan(document.body);
    observer = new MutationObserver(records => {
      collect(records);
      if (pending.size && !scheduled) {
        scheduled = true;
        setTimeout(flush, 0);
      }
    });
    observe();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
